export type DocumentSection = { heading: string; body: string }

export const BRAND_PROFILE_HEADINGS = [
  'Business name',
  'Business overview & positioning',
  'Market positioning',
  'Direct competitors',
  'Competitive advantages',
  'Primary customer segments',
  'Top revenue generators',
  'Emerging growth areas',
  'Primary value drivers',
  'Emotional benefits',
  'Brand story',
  'Brand personality'
] as const

const HEADING_LINE = /^## (.*)$/
const ESCAPED_HEADING_LINE = /^(\\+)## /

const escapeBody = (body: string) => body.replace(/^(\\*)## /gm, '\\$1## ')

const unescapeBody = (body: string) => body.replace(/^\\(\\*)## /gm, '$1## ')

export const parseDocument = (markdown: string): DocumentSection[] => {
  const sections: DocumentSection[] = []
  let current: { heading: string; lines: string[] } | undefined
  for (const line of markdown.split('\n')) {
    const heading = ESCAPED_HEADING_LINE.test(line) ? null : HEADING_LINE.exec(line)?.[1]
    if (heading !== null && heading !== undefined) {
      if (current) sections.push({ heading: current.heading, body: unescapeBody(current.lines.join('\n')) })
      current = { heading, lines: [] }
    } else current?.lines.push(line)
  }
  if (current) sections.push({ heading: current.heading, body: unescapeBody(current.lines.join('\n')) })
  return sections
}

export const serializeDocument = (sections: readonly DocumentSection[]): string =>
  sections
    .filter(section => section.heading.trim() !== '')
    .map(section => `## ${section.heading}\n${escapeBody(section.body)}`)
    .join('\n')

export const brandProfileSkeleton = (): string => serializeDocument(BRAND_PROFILE_HEADINGS.map(heading => ({ heading, body: '' })))

const isEmpty = (section: DocumentSection) => section.body.trim() === ''

export const mergeProfileDocument = (existing: readonly DocumentSection[], accepted: readonly DocumentSection[]): DocumentSection[] => {
  const merged = existing.map(section => ({ ...section }))
  for (const section of accepted) {
    const target = merged.find(candidate => candidate.heading === section.heading)
    if (target === undefined) merged.push({ ...section })
    else if (isEmpty(target)) target.body = section.body
  }
  return merged
}

export const sectionKey = (heading: string) => heading.trim().replace(/\s+/g, ' ').toLowerCase().replace(/[’‘]/g, "'")

const sectionHeaded = (sections: readonly DocumentSection[], heading: string) =>
  sections.find(section => sectionKey(section.heading) === sectionKey(heading))

export const readDocumentSection = (document: string, heading: string) => sectionHeaded(parseDocument(document), heading)?.body.trim() ?? ''

export const writeDocumentSection = (document: string, heading: string, body: string, order: readonly string[] = []) => {
  const sections = parseDocument(document)
  const target = sectionHeaded(sections, heading)
  if (target) target.body = body
  else {
    const rank = (candidate: string) => order.findIndex(entry => sectionKey(entry) === sectionKey(candidate))
    const after = rank(heading) < 0 ? -1 : sections.findIndex(section => rank(section.heading) > rank(heading))
    sections.splice(after < 0 ? sections.length : after, 0, { heading, body })
  }
  return serializeDocument(sections)
}
